import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useEmailVerification, isEmailUnverifiedError } from '../../hooks/useEmailVerification.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { ConfirmEmailNotice } from '../../components/ConfirmEmailNotice.jsx';
import { ProgramSteps, isTracked } from '../../components/ProgramSteps.jsx';
import { Button, Alert, Spinner } from '../../components/ui.jsx';

/**
 * Step 4: one look at what is about to go live, then publish.
 *
 * Only a confirmed email is required, as everywhere else. Missing tracking is a
 * warning, not a block: a brand may connect its store after publishing, and
 * saying so plainly beats refusing.
 */
export default function ProgramPublish() {
  const { id } = useParams();
  const programs = useFetch('/programs/mine/list');
  const integration = useFetch(`/programs/${id}/integration`);
  const { verified, refresh } = useEmailVerification();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const program = programs.data?.programs?.find((p) => p.id === id) ?? null;
  const base = `/dashboard/brand/programs/${id}`;

  async function publish() {
    setBusy(true);
    setError('');
    try {
      await api.patch(`/programs/${id}`, { status: 'active' });
      await programs.reload();
    } catch (err) {
      setError(err.message);
      if (isEmailUnverifiedError(err)) refresh().catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  if (programs.loading || integration.loading) {
    return (
      <DashboardLayout side="brand">
        <Spinner />
      </DashboardLayout>
    );
  }
  if (!program) {
    return (
      <DashboardLayout side="brand" title="Publish">
        <Alert>Program not found.</Alert>
      </DashboardLayout>
    );
  }

  const live = program.status === 'active';
  const tracked = isTracked(integration.data);
  const prizes = program.competition?.prizes?.length ?? 0;
  const checks = [
    {
      label: 'Terms',
      ok: true,
      detail: `${program.termsSummary} · ${program.visibility === 'private' ? 'private: invited and accepted creators only' : 'public'}`,
      to: base,
    },
    {
      label: 'Tracking',
      ok: tracked,
      detail: tracked
        ? 'Sales reach Affily.'
        : 'Nothing reports sales yet, so creators would earn nothing until it does.',
      to: `${base}/actions`,
    },
    {
      label: 'Prizes',
      ok: true,
      detail: prizes ? `${prizes} ${prizes === 1 ? 'prize' : 'prizes'}` : 'None — optional',
      to: `${base}/competition`,
    },
  ];

  return (
    <DashboardLayout side="brand" title="Publish" description={program.title}>
      <ProgramSteps programId={id} current="publish" program={program} />

      <div className="max-w-2xl space-y-6">
        <Alert>{error}</Alert>

        <ul className="divide-y divide-rule rounded-lg border border-rule bg-paper-raised">
          {checks.map((c) => (
            <li key={c.label} className="flex items-start justify-between gap-4 px-4 py-3">
              <div className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-2xs font-semibold ${
                    c.ok ? 'bg-positive-soft text-positive' : 'bg-caution-soft text-caution'
                  }`}
                >
                  {c.ok ? '✓' : '!'}
                </span>
                <div>
                  <p className="text-sm font-medium">{c.label}</p>
                  <p className="text-sm text-ink-muted">{c.detail}</p>
                </div>
              </div>
              <Link to={c.to} className="shrink-0 text-sm text-accent hover:underline">
                Edit
              </Link>
            </li>
          ))}
        </ul>

        {live ? (
          <Alert tone="positive">
            Live. Creators can find and apply to it.{' '}
            <Link to={`/programs/${program.slug}`} className="underline">
              View the public page
            </Link>
          </Alert>
        ) : (
          <div className="space-y-3">
            <Button onClick={publish} disabled={busy || !verified}>
              {busy ? 'Publishing…' : 'Publish program'}
            </Button>
            {!tracked && (
              <p className="text-xs text-ink-muted">
                You can publish now and connect your store after, but no sale is credited until
                you do.
              </p>
            )}
            <ConfirmEmailNotice />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
